import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { PRIVACY_PAGE } from "@/constants/testIds";
import { Footer } from "@/components/Footer";

const CONTENT = `# Aviso de Privacidad Integral de CERH

**Última actualización:** 6 de agosto de 2026

Este aviso aplica a CERH (Cumplimiento y Estrategia de RH) como empresa, y describe cómo tratamos los datos personales de las personas que visitan nuestro sitio, solicitan una demostración, o contratan y usan nuestra plataforma. No sustituye ni reemplaza el aviso de privacidad que cada empresa cliente (pyme) genera dentro de la plataforma para sus propios empleados — ese aviso lo controla cada cliente, no CERH directamente.

## 1. Identidad y domicilio del responsable

Payne Administrativo, S.A. de C.V. (RFC PAD1905301TA), con domicilio en Calle Renato Leduc No. 174, Int. 301, Col. Toriello Guerra, Alcaldía Tlalpan, C.P. 14050, Ciudad de México, México, es responsable del tratamiento de tus datos personales conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento.

## 2. Datos personales que recabamos

Dependiendo de tu relación con nosotros, podemos recabar:

- **Datos de identificación y contacto:** nombre, correo electrónico, teléfono, puesto, nombre de la empresa.
- **Datos de la empresa:** razón social, RFC, domicilio fiscal, representante legal (para generar documentos dentro de la plataforma).
- **Datos de facturación y pago:** los necesarios para procesar tu suscripción, a través de nuestro proveedor de pagos. CERH no almacena directamente los datos completos de tu tarjeta.
- **Datos de uso:** interacción con la plataforma, con fines de soporte, mejora del servicio y seguridad.

No solicitamos datos sensibles a través de nuestro sitio o proceso de registro.

## 3. Finalidades del tratamiento

**Finalidades primarias (necesarias para el servicio):**

- Crear y administrar tu cuenta y la de tu empresa dentro de CERH.
- Procesar pagos y facturación de tu suscripción.
- Brindar soporte técnico y atención a solicitudes.
- Cumplir obligaciones legales y contractuales derivadas del servicio.
- Enviar comunicaciones operativas relacionadas con tu cuenta (avisos de cobro, cambios al servicio, seguridad).

**Finalidades secundarias (opcionales):**

- Enviarte información comercial, novedades o contenido educativo sobre RRHH y cumplimiento en México.

Puedes oponerte a las finalidades secundarias sin que esto afecte tu acceso al servicio, escribiendo a contacto@cerh.com.mx con el asunto "Cancelar comunicaciones".

## 4. Nuestro rol frente a los datos de los empleados de tu empresa

Cuando usas CERH para administrar a tus empleados (expedientes, políticas, incidencias, nómina), CERH actúa como **encargado** de esos datos, no como responsable. Tú, como empresa cliente, decides qué datos de tus empleados se capturan y con qué fin, y eres responsable frente a ellos conforme a tu propio aviso de privacidad, generado dentro de la plataforma. CERH trata esos datos únicamente para prestarte el servicio contratado, con las medidas de seguridad correspondientes, y no los usa para fines propios ajenos a la prestación del servicio. El detalle de esta relación como encargado está en nuestro [Acuerdo de Tratamiento de Datos](/acuerdo-tratamiento-datos).

## 5. Transferencias de datos y medidas de seguridad

Para operar el servicio, compartimos datos con proveedores que actúan como encargados en nuestro nombre, entre ellos:

- Google Cloud Platform, que aloja nuestra infraestructura de aplicación y base de datos en la región us-central1 (Council Bluffs, Iowa, Estados Unidos).
- Stripe, Inc., que procesa los pagos de tu suscripción.
- Resend, que envía los correos automáticos de la plataforma (confirmaciones, avisos de cobro, recuperación de contraseña).

Esto implica que tus datos se almacenan y procesan fuera de México, en Estados Unidos. Esta transferencia no requiere tu consentimiento por tratarse de encargados que tratan los datos exclusivamente para cumplir la relación jurídica que tenemos contigo, conforme al artículo 37 de la LFPDPPP. No vendemos ni compartimos tus datos con terceros para fines de mercadotecnia ajenos a CERH.

Nuestra infraestructura cifra tus datos en tránsito mediante TLS/HTTPS y en reposo mediante el estándar AES-256, de forma automática y sin configuración adicional de nuestra parte. Los respaldos de la base de datos se generan de forma horaria, diaria, semanal y mensual, con recuperación a un punto exacto en el tiempo dentro de los últimos 7 días. Nuestro proveedor de infraestructura cuenta con certificaciones ISO 27001 y SOC 2.

Si cancelas tu cuenta, conservamos el expediente de tu empresa durante 90 días naturales después de la cancelación, para que puedas reactivarla o solicitar la exportación de tus datos. Transcurrido ese plazo, la información se elimina de forma definitiva de nuestros sistemas activos, sujeta a los respaldos históricos descritos en el párrafo anterior, los cuales se eliminan conforme a su propio calendario de retención.

## 6. Derechos ARCO

Tienes derecho a Acceder, Rectificar, Cancelar u Oponerte (ARCO) al tratamiento de tus datos personales. Para ejercer estos derechos, escribe a **contacto@cerh.com.mx** indicando tu nombre completo, el derecho que deseas ejercer, y una descripción clara de tu solicitud. Te responderemos en los plazos que marca la ley.

También puedes revocar tu consentimiento para el tratamiento de tus datos en cualquier momento, a través del mismo correo, considerando que esto puede implicar la cancelación de tu cuenta si los datos son necesarios para prestarte el servicio.

## 7. Uso de cookies y tecnologías de rastreo

Nuestro sitio puede usar cookies u otras tecnologías similares para mejorar tu experiencia de navegación y entender el uso del sitio. Puedes deshabilitarlas desde la configuración de tu navegador.

## 8. Cambios a este aviso

Podemos actualizar este aviso de privacidad. Cualquier cambio relevante se publicará en esta misma página con su fecha de actualización.

## 9. Contacto

Para dudas sobre este aviso o el tratamiento de tus datos: **contacto@cerh.com.mx**`;

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 md:px-12 py-6">
        <Link to="/" className="flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal tracking-tight">CERH</span>
        </Link>
      </header>
      <section className="px-6 md:px-12 pb-20 max-w-3xl mx-auto">
        <div data-testid={PRIVACY_PAGE.content} className="prose prose-slate max-w-none">
          <ReactMarkdown
            components={{
              h1: ({ children }) => <h1 className="font-heading text-3xl font-bold text-charcoal mb-4">{children}</h1>,
              h2: ({ children }) => <h2 className="font-heading text-xl font-semibold text-charcoal mt-8 mb-3">{children}</h2>,
              p: ({ children }) => <p className="text-sm text-charcoal/75 leading-relaxed mb-3">{children}</p>,
              ul: ({ children }) => <ul className="list-disc pl-5 text-sm text-charcoal/75 space-y-1.5 mb-3">{children}</ul>,
              li: ({ children }) => <li>{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-charcoal">{children}</strong>,
              hr: () => <hr className="my-8 border-petroleum/10" />,
            }}
          >
            {CONTENT}
          </ReactMarkdown>
        </div>
      </section>
      <Footer />
    </div>
  );
}
